import React, { useState } from "react";
import {
  Users,
  DollarSign,
  Scale,
  Calendar,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  UserCheck,
  Megaphone,
  Building,
} from "lucide-react";
import { useData } from "../context/DataContext";
import { useToast } from "../context/ToastContext";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Button from "../components/ui/Button";
import Modal from "../components/ui/Modal";
import Input from "../components/ui/Input";
import { format } from "date-fns";

export default function Dashboard() {
  const { state, dispatch } = useData();
  const { showSuccess } = useToast();

  // Quick Actions modal states
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showScheduleMeetingModal, setShowScheduleMeetingModal] =
    useState(false);
  const [showOpenCaseModal, setShowOpenCaseModal] = useState(false);
  const [showSendAnnouncementModal, setShowSendAnnouncementModal] =
    useState(false);

  // Calculate KPIs
  const totalMembers = state.members.length;
  const activeMembers = state.members.filter(
    (m) => m.licenseStatus === "active"
  ).length;
  const expiredLicenses = state.members.filter(
    (m) => m.licenseStatus === "expired"
  ).length;

  const totalIncome = state.financialRecords
    .filter((r) => r.type === "income")
    .reduce((sum, r) => sum + r.amount, 0);

  const totalExpenses = state.financialRecords
    .filter((r) => r.type === "expense")
    .reduce((sum, r) => sum + r.amount, 0);

  const netIncome = totalIncome - totalExpenses;

  const openCases = state.cases.filter(
    (c) => c.status === "open" || c.status === "in-progress"
  ).length;

  const upcomingMeetings = state.meetings.filter(
    (m) => new Date(m.date) > new Date() && m.status === "scheduled"
  ).length;

  const upcomingEvents = state.events.filter(
    (e) => new Date(e.date) > new Date() && e.status === "planned"
  ).length;

  // Quick Actions handlers
  const handleAddMember = (memberData: any) => {
    const newMember = {
      ...memberData,
      id: Date.now().toString(),
      joinDate: new Date().toISOString().split("T")[0],
      licenseStatus: "active" as const,
    };
    dispatch({ type: "ADD_MEMBER", payload: newMember });
    setShowAddMemberModal(false);
  };

  const handleScheduleMeeting = (meetingData: any) => {
    const newMeeting = {
      ...meetingData,
      id: Date.now().toString(),
      status: "scheduled" as const,
    };
    dispatch({ type: "ADD_MEETING", payload: newMeeting });
    setShowScheduleMeetingModal(false);
  };

  const handleOpenCase = (caseData: any) => {
    const newCase = {
      ...caseData,
      id: Date.now().toString(),
      status: "open" as const,
      createdDate: new Date().toISOString().split("T")[0],
    };
    dispatch({ type: "ADD_CASE", payload: newCase });
    setShowOpenCaseModal(false);
  };

  const handleSendAnnouncement = (announcementData: any) => {
    // For now, we'll just show a success message
    // In a real app, this would send emails/notifications
    showSuccess(
      "Announcement Sent",
      `Announcement "${announcementData.title}" has been sent successfully!`
    );
    setShowSendAnnouncementModal(false);
  };

  const kpiCards = [
    {
      title: "Total Members",
      value: totalMembers,
      change: "+12%",
      changeType: "positive" as const,
      icon: Users,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    {
      title: "Active Members",
      value: activeMembers,
      change: `${((activeMembers / totalMembers) * 100).toFixed(1)}%`,
      changeType: "positive" as const,
      icon: UserCheck,
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
    {
      title: "Net Income",
      value: `EGP ${netIncome.toLocaleString()}`,
      change: "+8.2%",
      changeType: "positive" as const,
      icon: DollarSign,
      color: "text-emerald-600",
      bgColor: "bg-emerald-100",
    },
    {
      title: "Open Cases",
      value: openCases,
      change: "-3",
      changeType: "negative" as const,
      icon: Scale,
      color: "text-orange-600",
      bgColor: "bg-orange-100",
    },
  ];

  const recentActivities = [
    ...state.members.slice(0, 3).map((member) => ({
      id: `member-${member.id}`,
      type: "member" as const,
      title: `New member registered: ${member.name}`,
      time: member.joinDate,
      icon: Users,
    })),
    ...state.cases.slice(0, 2).map((case_) => ({
      id: `case-${case_.id}`,
      type: "case" as const,
      title: `New case opened: ${case_.title}`,
      time: case_.createdDate,
      icon: Scale,
    })),
    ...state.meetings.slice(0, 2).map((meeting) => ({
      id: `meeting-${meeting.id}`,
      type: "meeting" as const,
      title: `Meeting scheduled: ${meeting.title}`,
      time: meeting.date,
      icon: Calendar,
    })),
  ]
    .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">
          Welcome to the Syndicate of Pilots ERP System
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiCards.map((kpi, index) => (
          <Card key={index} className="relative overflow-hidden">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{kpi.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {kpi.value}
                </p>
                <div className="flex items-center mt-2">
                  <TrendingUp
                    className={`h-4 w-4 ${
                      kpi.changeType === "positive"
                        ? "text-green-500"
                        : "text-red-500"
                    }`}
                  />
                  <span
                    className={`text-sm ml-1 ${
                      kpi.changeType === "positive"
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {kpi.change}
                  </span>
                </div>
              </div>
              <div className={`p-3 rounded-lg ${kpi.bgColor}`}>
                <kpi.icon className={`h-6 w-6 ${kpi.color}`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Membership Status */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Membership Status
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                <span className="text-sm text-gray-600">Active Licenses</span>
              </div>
              <span className="font-semibold text-gray-900">
                {activeMembers}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                <span className="text-sm text-gray-600">Expired Licenses</span>
              </div>
              <span className="font-semibold text-gray-900">
                {expiredLicenses}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-yellow-500 mr-2" />
                <span className="text-sm text-gray-600">Pending Renewals</span>
              </div>
              <span className="font-semibold text-gray-900">3</span>
            </div>
          </div>
        </Card>

        {/* Financial Overview */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Financial Overview
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Income</span>
              <span className="font-semibold text-green-600">
                EGP {totalIncome.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Expenses</span>
              <span className="font-semibold text-red-600">
                EGP {totalExpenses.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <span className="text-sm font-medium text-gray-900">
                Net Income
              </span>
              <span
                className={`font-bold ${
                  netIncome >= 0 ? "text-green-600" : "text-red-600"
                }`}
              >
                EGP {netIncome.toLocaleString()}
              </span>
            </div>
          </div>
        </Card>

        {/* Upcoming Events */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Upcoming Events
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Calendar className="h-5 w-5 text-blue-500 mr-2" />
                <span className="text-sm text-gray-600">Meetings</span>
              </div>
              <span className="font-semibold text-gray-900">
                {upcomingMeetings}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Building className="h-5 w-5 text-purple-500 mr-2" />
                <span className="text-sm text-gray-600">Events</span>
              </div>
              <span className="font-semibold text-gray-900">
                {upcomingEvents}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Scale className="h-5 w-5 text-orange-500 mr-2" />
                <span className="text-sm text-gray-600">Open Cases</span>
              </div>
              <span className="font-semibold text-gray-900">{openCases}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Activities */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Recent Activities
        </h3>
        <div className="space-y-4">
          {recentActivities.map((activity) => (
            <div
              key={activity.id}
              className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex-shrink-0">
                <activity.icon className="h-5 w-5 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  {activity.title}
                </p>
                <p className="text-sm text-gray-500">
                  {format(new Date(activity.time), "MMM dd, yyyy")}
                </p>
              </div>
              <Badge variant="info" size="sm">
                {activity.type}
              </Badge>
            </div>
          ))}
        </div>
      </Card>

      {/* Quick Actions */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Quick Actions
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => setShowAddMemberModal(true)}
            className="p-4 text-center bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
          >
            <Users className="h-8 w-8 text-primary-600 mx-auto mb-2" />
            <span className="text-sm font-medium text-primary-700">
              Add Member
            </span>
          </button>
          <button
            onClick={() => setShowScheduleMeetingModal(true)}
            className="p-4 text-center bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
          >
            <Calendar className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <span className="text-sm font-medium text-green-700">
              Schedule Meeting
            </span>
          </button>
          <button
            onClick={() => setShowOpenCaseModal(true)}
            className="p-4 text-center bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors"
          >
            <Scale className="h-8 w-8 text-orange-600 mx-auto mb-2" />
            <span className="text-sm font-medium text-orange-700">
              Open Case
            </span>
          </button>
          <button
            onClick={() => setShowSendAnnouncementModal(true)}
            className="p-4 text-center bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
          >
            <Megaphone className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <span className="text-sm font-medium text-purple-700">
              Send Announcement
            </span>
          </button>
        </div>
      </Card>

      {/* Quick Actions Modals */}
      <AddMemberModal
        isOpen={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        onAdd={handleAddMember}
      />

      <ScheduleMeetingModal
        isOpen={showScheduleMeetingModal}
        onClose={() => setShowScheduleMeetingModal(false)}
        onSchedule={handleScheduleMeeting}
      />

      <OpenCaseModal
        isOpen={showOpenCaseModal}
        onClose={() => setShowOpenCaseModal(false)}
        onOpen={handleOpenCase}
      />

      <SendAnnouncementModal
        isOpen={showSendAnnouncementModal}
        onClose={() => setShowSendAnnouncementModal(false)}
        onSend={handleSendAnnouncement}
      />
    </div>
  );
}

// Add Member Modal Component
function AddMemberModal({
  isOpen,
  onClose,
  onAdd,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (memberData: any) => void;
}) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    licenseNumber: "",
    airline: "",
    position: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(formData);
    setFormData({
      name: "",
      email: "",
      phone: "",
      licenseNumber: "",
      airline: "",
      position: "",
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Member" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Full Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            required
          />
          <Input
            label="Phone"
            value={formData.phone}
            onChange={(e) =>
              setFormData({ ...formData, phone: e.target.value })
            }
            required
          />
          <Input
            label="License Number"
            value={formData.licenseNumber}
            onChange={(e) =>
              setFormData({ ...formData, licenseNumber: e.target.value })
            }
            required
          />
          <Input
            label="Airline"
            value={formData.airline}
            onChange={(e) =>
              setFormData({ ...formData, airline: e.target.value })
            }
            required
          />
          <Input
            label="Position"
            value={formData.position}
            onChange={(e) =>
              setFormData({ ...formData, position: e.target.value })
            }
            required
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Add Member</Button>
        </div>
      </form>
    </Modal>
  );
}

// Schedule Meeting Modal Component
function ScheduleMeetingModal({
  isOpen,
  onClose,
  onSchedule,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSchedule: (meetingData: any) => void;
}) {
  const [formData, setFormData] = useState({
    title: "",
    date: "",
    time: "",
    location: "",
    description: "",
    attendees: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSchedule(formData);
    setFormData({
      title: "",
      date: "",
      time: "",
      location: "",
      description: "",
      attendees: "",
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Schedule Meeting" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Meeting Title"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            required
          />
          <Input
            label="Date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
          />
          <Input
            label="Time"
            type="time"
            value={formData.time}
            onChange={(e) => setFormData({ ...formData, time: e.target.value })}
            required
          />
          <Input
            label="Location"
            value={formData.location}
            onChange={(e) =>
              setFormData({ ...formData, location: e.target.value })
            }
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <Input
          label="Attendees (comma-separated)"
          value={formData.attendees}
          onChange={(e) =>
            setFormData({ ...formData, attendees: e.target.value })
          }
          placeholder="John Doe, Jane Smith, etc."
        />

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Schedule Meeting</Button>
        </div>
      </form>
    </Modal>
  );
}

// Open Case Modal Component
function OpenCaseModal({
  isOpen,
  onClose,
  onOpen,
}: {
  isOpen: boolean;
  onClose: () => void;
  onOpen: (caseData: any) => void;
}) {
  const [formData, setFormData] = useState({
    title: "",
    complainant: "",
    respondent: "",
    type: "employment",
    priority: "medium",
    description: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onOpen(formData);
    setFormData({
      title: "",
      complainant: "",
      respondent: "",
      type: "employment",
      priority: "medium",
      description: "",
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Open New Case" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Case Title"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            required
          />
          <Input
            label="Complainant"
            value={formData.complainant}
            onChange={(e) =>
              setFormData({ ...formData, complainant: e.target.value })
            }
            required
          />
          <Input
            label="Respondent"
            value={formData.respondent}
            onChange={(e) =>
              setFormData({ ...formData, respondent: e.target.value })
            }
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Case Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="employment">Employment</option>
              <option value="contract">Contract</option>
              <option value="disciplinary">Disciplinary</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={formData.priority}
              onChange={(e) =>
                setFormData({ ...formData, priority: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Case Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Open Case</Button>
        </div>
      </form>
    </Modal>
  );
}

// Send Announcement Modal Component
function SendAnnouncementModal({
  isOpen,
  onClose,
  onSend,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSend: (announcementData: any) => void;
}) {
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    recipients: "all",
    priority: "normal",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSend(formData);
    setFormData({
      title: "",
      message: "",
      recipients: "all",
      priority: "normal",
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Announcement"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label="Announcement Title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Message
          </label>
          <textarea
            value={formData.message}
            onChange={(e) =>
              setFormData({ ...formData, message: e.target.value })
            }
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recipients
            </label>
            <select
              value={formData.recipients}
              onChange={(e) =>
                setFormData({ ...formData, recipients: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Members</option>
              <option value="active">Active Members Only</option>
              <option value="committee">Committee Members</option>
              <option value="specific">Specific Members</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={formData.priority}
              onChange={(e) =>
                setFormData({ ...formData, priority: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Send Announcement</Button>
        </div>
      </form>
    </Modal>
  );
}
